/**
 * Chapter illustrations shown during playback — pure logic, kept separate
 * from AudioPlayerContext and the pages that render it so it can be unit
 * tested (see src/lib/chapterScenes.test.ts) without mounting a real
 * <audio> element or any DOM. Mirrors the shape of src/lib/sleepTimer.ts.
 *
 * A chapter's image is optional (older backend: absent: newer backend, no
 * illustration for that chapter yet: present but null) — both are treated
 * as "no image".
 *
 * Product rule: once a story has at least one chapter image anywhere
 * (`hasAnyChapterImage`), the player area always shows a chapter image,
 * never the cover — there is no "cover vs chapter images" choice for the
 * listener to make. A chapter with no image of its own keeps showing the
 * most recent earlier chapter's image; before that has happened yet
 * (intro, or an early chapter with no image), it shows the first chapter
 * image the story has. Only a story with zero chapter images anywhere
 * shows the cover, exactly as it always has.
 */
import type { StoryChapter } from "./types";

export interface SceneChapter {
  /** Seconds from the start of the track. */
  startS: number;
  imageUrl?: string | null;
  imageThumbUrl?: string | null;
}

function hasImage(chapter: SceneChapter | null | undefined): chapter is SceneChapter {
  return !!chapter && (!!chapter.imageUrl || !!chapter.imageThumbUrl);
}

/** Adapts the API's `StoryChapter[]` (snake_case `start_s`/`image_url`) to
 *  this module's own shape — same reasoning as `sleepTimer.ts`'s
 *  `SleepTimerChapter`: the decision logic above stays testable with plain
 *  literals, with no dependency on the wider API types drifting underneath
 *  it. Callers (`ChapterScene`, the pages) map through this once. */
export function toSceneChapters(chapters: StoryChapter[]): SceneChapter[] {
  return chapters.map((c) => (
    { startS: c.start_s, imageUrl: c.image_url ?? null, imageThumbUrl: c.image_thumb_url ?? null }
  ));
}

function sortedByStart(chapters: SceneChapter[]): SceneChapter[] {
  return [...chapters].sort((a, b) => a.startS - b.startS);
}

/**
 * Which chapter's image should be displayed right now, or null when the
 * story cover should be shown instead — only when the story has no chapter
 * image anywhere. Otherwise this always resolves to an image: the latest
 * chapter at or before `positionS` that carries one, or, when none at or
 * before that position has one yet (before playback reaches the first
 * chapter image), the first chapter image the story has. A chapter with no
 * image of its own is never itself returned: it always resolves to the
 * nearest earlier chapter image instead.
 */
export function currentSceneChapter(chapters: SceneChapter[], positionS: number): SceneChapter | null {
  const sorted = sortedByStart(chapters);
  let found: SceneChapter | null = null;
  for (const c of sorted) {
    if (c.startS <= positionS && hasImage(c)) found = c;
  }
  if (found) return found;
  return sorted.find(hasImage) ?? null;
}

/** True as soon as any chapter of the story carries an image — used to
 *  decide whether the illustration UI mounts at all. A story with no
 *  chapter images anywhere must render pixel-identical to before the
 *  feature existed, so callers gate on this rather than always rendering
 *  the (otherwise no-op) crossfade wrapper. */
export function hasAnyChapterImage(chapters: SceneChapter[]): boolean {
  return chapters.some(hasImage);
}

/**
 * The next chapter (by `startS`, strictly after the current position) that
 * has an image, so the caller can preload it ahead of the crossfade — or
 * null when there is no such chapter (last chapter already showing, or
 * none of the chapters after this point carry an image). Chapters with no
 * image are skipped over rather than stopping the search: preloading only
 * ever targets an actual upcoming scene.
 */
export function nextSceneChapter(chapters: SceneChapter[], positionS: number): SceneChapter | null {
  const sorted = sortedByStart(chapters);
  const upcoming = sorted.find((c) => c.startS > positionS && hasImage(c));
  return upcoming ?? null;
}

/** The src to actually load for a chapter that `hasImage` — the full-size
 *  1024px image over the 400px thumb, same "biggest available" rule as
 *  `coverImageSrc("full", ...)`. Shared by `<ChapterScene>` (the small
 *  cover-slot illustration) and `<ChapterSceneFullscreen>` so the two
 *  never disagree on which src is "current" for the same chapter. */
export function sceneImageSrc(chapter: SceneChapter): string {
  return (chapter.imageUrl || chapter.imageThumbUrl) as string;
}
